import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Select, Space, Spin, Typography } from 'antd';

import {
  fetchSimplifiedOptionPremiumModel,
  SIMPLIFIED_OPTION_PREMIUM_MODEL_OPTIONS,
  SimplifiedOptionPremiumModel,
  updateSimplifiedOptionPremiumModel,
} from './api';

type Props = {
  sessionId: number;
  readOnly?: boolean;
};

/** Render the premium-model control only inside PM's administrator workflows. */
const SessionPremiumModelCard: React.FC<Props> = ({
  sessionId,
  readOnly = false,
}) => {
  const [premiumModel, setPremiumModel] =
    useState<SimplifiedOptionPremiumModel>();
  const [persistedModel, setPersistedModel] =
    useState<SimplifiedOptionPremiumModel>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;

    /** Load the private field separately so generic session state remains unchanged. */
    const loadPremiumModel = async () => {
      setLoading(true);
      setError(undefined);
      setPremiumModel(undefined);
      setPersistedModel(undefined);
      try {
        const result = await fetchSimplifiedOptionPremiumModel(sessionId);
        if (!active) return;
        setPremiumModel(result.simplified_option_premium_model);
        setPersistedModel(result.simplified_option_premium_model);
      } catch (requestError) {
        if (!active) return;
        setError(
          requestError instanceof Error
            ? requestError.message
            : 'The premium model could not be loaded.',
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    loadPremiumModel();
    return () => {
      active = false;
    };
  }, [sessionId]);

  /** Save only a changed enum value so opening the admin page never mutates the session. */
  const savePremiumModel = async () => {
    if (!premiumModel || premiumModel === persistedModel || readOnly) return;

    setSaving(true);
    setError(undefined);
    try {
      const result = await updateSimplifiedOptionPremiumModel(
        sessionId,
        premiumModel,
      );
      setPremiumModel(result.simplified_option_premium_model);
      setPersistedModel(result.simplified_option_premium_model);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'The premium model could not be saved.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      size="small"
      title="Simplified options pricing"
      style={{ marginTop: 16, marginBottom: 16 }}
    >
      <Space orientation="vertical" size={8} style={{ width: '100%' }}>
        <Typography.Text type="secondary">
          Select the economic model used when future issuance quotes are frozen.
          Existing frozen quotes are not repriced by this setting.
        </Typography.Text>
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <Spin spinning={loading}>
          <Space wrap>
            <Select
              aria-label="Simplified options premium model"
              value={premiumModel}
              options={SIMPLIFIED_OPTION_PREMIUM_MODEL_OPTIONS}
              disabled={readOnly || loading || saving}
              style={{ minWidth: 220 }}
              onChange={value => {
                setPremiumModel(value);
                setError(undefined);
              }}
            />
            <Button
              type="primary"
              loading={saving}
              disabled={
                readOnly ||
                loading ||
                !premiumModel ||
                premiumModel === persistedModel
              }
              onClick={savePremiumModel}
            >
              Save premium model
            </Button>
          </Space>
        </Spin>
        {readOnly ? (
          <Typography.Text type="secondary">
            This blueprint version is locked, so its premium model is read-only.
          </Typography.Text>
        ) : null}
      </Space>
    </Card>
  );
};

export default SessionPremiumModelCard;
